import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';

import { CONTACT_GROUP_LABELS, ContactGroup, ContactSourceKind } from 'constants/contactSources';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';

import {
  BORROWING_ENTITY_TABS_HREF,
  ENTITY_TYPE_HREF as BORROWER_ENTITY_TYPE_HREF,
  NBE_ENTITY_TABS_HREF,
} from '../borrowers/EditEntityModal/EditEntityModal.hrefs';
import { SECTION_HREF_MAP as CLOSING_SECTION_HREF_MAP } from '../loanClosingCDS/EditDrawer/EditDrawer.hrefs';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_SECTION_HREF_MAP,
} from '../propertyAndLoan/EditModal/EditModal.hrefs';

export const LOADING_ROWS = [];

export const PartyColumnId = {
  PARTY: 'party',
  CONTACT_TYPE: 'contactType',
  SOURCE: 'source',
  CONTACT_INFO: 'contactInfo',
  ADDRESS: 'address',
  DRIVE: 'drive',
  ACTION: 'action',
};

export const PARTY_COLUMN_LABELS = {
  [PartyColumnId.PARTY]: 'Party',
  [PartyColumnId.CONTACT_TYPE]: 'Contact type',
  [PartyColumnId.SOURCE]: 'Source',
  [PartyColumnId.CONTACT_INFO]: 'Email & phone',
  [PartyColumnId.ADDRESS]: 'Address',
  [PartyColumnId.DRIVE]: 'Sent to DRIVE as',
  [PartyColumnId.ACTION]: 'Action',
};

export const PartyDriveState = {
  PENDING: 'pending',
  MAPPING_DISABLED: 'mappingDisabled',
  BORROWER: 'borrower',
  SENT: 'sent',
  OVERFLOW: 'overflow',
  NOT_MAPPED: 'notMapped',
  NOT_SENT: 'notSent',
};

export const PARTY_DRIVE_LABEL_VARIANTS = {
  [PartyDriveState.OVERFLOW]: LabelVariant.RED,
  [PartyDriveState.NOT_MAPPED]: LabelVariant.ORANGE,
  [PartyDriveState.NOT_SENT]: LabelVariant.GRAY,
};

export const NOT_APPLICABLE_TEXT = '—';

export const BORROWER_DRIVE_TEXT = 'Borrower';

export const NOT_MAPPED_TEXT = 'Not mapped';

export const NOT_SENT_TEXT = 'Not sent';

export const UNNAMED_PARTY_TEXT = 'Unnamed party';

export const PARTY_GROUP_LABELS = {
  ...CONTACT_GROUP_LABELS,
  [ContactGroup.BORROWERS_AND_LEADS]: 'Borrowers',
};

export const PAGE_TITLE = 'Contacts';

export const NO_PARTIES_MESSAGE = 'No contacts on this loan yet';

export const ADD_CONTACT_BUTTON_LABEL = 'Add contact';

// A loan-file contact change waits as a pending change until the loan's pending changes are saved.
export const PENDING_CONTACT_LABELS = {
  [PendingChangesActionType.ADDED]: 'Pending add',
  [PendingChangesActionType.MODIFIED]: 'Pending edit',
  [PendingChangesActionType.DELETED]: 'Pending removal',
};

export const EDIT_CONTACT_MENU_LABEL = 'Edit';

export const REMOVE_CONTACT_MENU_LABEL = 'Remove';

export const REMOVE_CONTACT_DIALOG_MESSAGE = 'Remove contact';

export const REMOVE_CONTACT_SUCCESS_MESSAGE = 'Contact is successfully removed';

const PROPERTY_AND_LOAN_TAB_ROUTE = LoanApplicationTabRouteParam.PROPERTY_AND_LOAN;
const BORROWERS_TAB_ROUTE = LoanApplicationTabRouteParam.BORROWERS;
const CLOSING_TAB_ROUTE = LoanApplicationTabRouteParam.CLOSING_NEW;

const AGENT_AND_SELLER_LOCATION = {
  label: 'Edit in Agent & Seller',
  getPathParams: () => ({
    tab: PROPERTY_AND_LOAN_TAB_ROUTE,
    subtab: ENTITY_TYPE_HREF_MAP.PROPERTY_INFO,
    entityId: PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER,
  }),
};

// Where the loan editor edits a party that comes from another part of the loan, by its sourceKind.
// Loan-file contacts are edited on the Contacts tab itself, so they have no entry here.
export const PARTY_EDIT_LOCATIONS = {
  [ContactSourceKind.BORROWER]: {
    label: 'Edit in Borrowers',
    getPathParams: sourceId => ({
      tab: BORROWERS_TAB_ROUTE,
      subtab: BORROWER_ENTITY_TYPE_HREF.BORROWER,
      entityId: sourceId,
      section: BORROWING_ENTITY_TABS_HREF.BORROWER,
    }),
  },
  [ContactSourceKind.NON_BORROWING_ENTITY]: {
    label: 'Edit in Borrowers',
    getPathParams: sourceId => ({
      tab: BORROWERS_TAB_ROUTE,
      subtab: BORROWER_ENTITY_TYPE_HREF.NON_BORROWER,
      entityId: sourceId,
      section: NBE_ENTITY_TABS_HREF.NBE_INFO,
    }),
  },
  [ContactSourceKind.LOAN_ROLE]: {
    label: 'Edit in Loan Roles',
    getPathParams: () => ({
      tab: PROPERTY_AND_LOAN_TAB_ROUTE,
      subtab: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
      entityId: LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_ROLES,
    }),
  },
  [ContactSourceKind.AGENT]: AGENT_AND_SELLER_LOCATION,
  [ContactSourceKind.SELLER]: AGENT_AND_SELLER_LOCATION,
  [ContactSourceKind.REALTOR]: AGENT_AND_SELLER_LOCATION,
  [ContactSourceKind.CLOSING_COMPANY]: {
    label: 'Edit in Closing',
    getPathParams: () => ({
      tab: CLOSING_TAB_ROUTE,
      subtab: CLOSING_SECTION_HREF_MAP.TITLE_COMPANY,
    }),
  },
  [ContactSourceKind.TITLE_HOLDER]: {
    label: 'Edit in Closing',
    getPathParams: () => ({
      tab: CLOSING_TAB_ROUTE,
      subtab: CLOSING_SECTION_HREF_MAP.TITLE_HOLDER,
    }),
  },
};

export const PREVIEW_BUTTON_LABEL = 'Preview DRIVE request';

export const PREVIEW_SCROLL_OPTIONS = { behavior: 'smooth', block: 'start' };
